import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import {
  Button,
  cx,
  EmptyState,
  ErrorAlert,
  Field,
  Form,
  Input,
  Modal,
  Section,
  useToast,
  type Requirement,
} from "@/components/ui";
import { RuleRow } from "@/components/domains/parts";
import { domainKeys, domainsApi, type DnsPreview, type Domain, type UrlForward } from "@/lib/domains";

import { BatchReviewModal } from "../dns/BatchReviewModal";
import { fullHost } from "../dns/util";

interface Draft {
  host: string;
  target_url: string;
  redirect: "301" | "302";
  keep_path: boolean;
}

function draftFor(f: UrlForward | null): Draft {
  return f
    ? { host: f.host, target_url: f.target_url, redirect: f.redirect, keep_path: f.keep_path }
    : { host: "@", target_url: "https://", redirect: "301", keep_path: false };
}

/** URL forwarding (PHP-3942, AC4-5): preview the records it replaces, then confirm (AC12). */
export function UrlForwardingSection({
  domain,
  canManage,
  manage,
}: {
  domain: Domain;
  canManage: boolean;
  manage: Requirement;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const listKey = [...domainKeys.all(orgId), "url-forwards", domain.id];
  const list = useQuery({ queryKey: listKey, queryFn: () => domainsApi.urlForwards(orgId, domain.id) });

  const [draft, setDraft] = useState<{ editingHost: string | null; value: Draft } | null>(null);
  const [review, setReview] = useState<{ draft: Draft; preview: DnsPreview } | null>(null);
  const [removing, setRemoving] = useState<UrlForward | null>(null);

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: domainKeys.all(orgId) }),
      qc.invalidateQueries({ queryKey: domainKeys.zone(orgId, domain.id) }),
    ]);

  const preview = useMutation({
    mutationFn: (d: Draft) => domainsApi.previewUrlForward(orgId, domain.id, d.host),
    onSuccess: (result, d) => setReview({ draft: d, preview: result }),
  });
  const save = useMutation({
    mutationFn: (d: Draft) => domainsApi.setUrlForward(orgId, domain.id, d),
    onSuccess: async () => {
      await invalidate();
      toast.show({ tone: "success", title: "URL forwarding saved" });
      setDraft(null);
      setReview(null);
      preview.reset();
    },
  });
  const remove = useMutation({
    mutationFn: (host: string) => domainsApi.removeUrlForward(orgId, domain.id, host),
    onSuccess: async () => {
      await invalidate();
      toast.show({ tone: "success", title: "URL forwarding removed" });
      setRemoving(null);
    },
  });

  const closeDraft = () => {
    if (preview.isPending) return;
    setDraft(null);
    preview.reset();
  };
  const closeReview = () => {
    if (save.isPending) return;
    setReview(null);
    save.reset();
  };

  return (
    <Section
      title="URL forwarding"
      subtitle="Send visitors at a host on this domain to another web address"
      action={
        canManage && (
          <Button
            size="lg"
            variant="secondary"
            requires={manage}
            onClick={() => setDraft({ editingHost: null, value: draftFor(null) })}
          >
            Add forwarding
          </Button>
        )
      }
    >
      {list.isPending ? (
        <p className="text-[13px] text-fg-subtle">Loading…</p>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.forwards.length === 0 ? (
        <EmptyState
          title="No URL forwards yet"
          description="Send visitors at this domain, or a subdomain, to another web address."
          action={
            canManage && (
              <Button
                variant="secondary"
                onClick={() => setDraft({ editingHost: null, value: draftFor(null) })}
              >
                Add forwarding
              </Button>
            )
          }
        />
      ) : (
        list.data.forwards.map((f, i) => (
          <RuleRow key={f.id} first={i === 0}>
            <div className="min-w-0 flex-1">
              <p className="text-sm break-all text-fg">
                {fullHost(f.host, domain.fqdn)} → {f.target_url}
              </p>
              <p className="text-xs text-fg-subtle">
                {f.redirect} redirect{f.keep_path ? " · keeps the page path" : ""}
              </p>
            </div>
            {canManage && (
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  requires={manage}
                  onClick={() => setDraft({ editingHost: f.host, value: draftFor(f) })}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-[#b52522] hover:text-[#b52522]"
                  requires={manage}
                  onClick={() => setRemoving(f)}
                >
                  Remove
                </Button>
              </div>
            )}
          </RuleRow>
        ))
      )}

      {draft && (
        <Modal
          open
          onClose={closeDraft}
          title={draft.editingHost !== null ? "Edit URL forwarding" : "Add URL forwarding"}
          note="You'll see what this replaces before it's applied."
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={closeDraft}>
                Cancel
              </Button>
              <Button
                variant="brand"
                size="lg"
                type="submit"
                form="url-forward-form"
                loading={preview.isPending}
              >
                Review change
              </Button>
            </>
          }
        >
          <Form
            id="url-forward-form"
            className="flex flex-col gap-4"
            error={preview.error}
            onSubmit={(e) => {
              e.preventDefault();
              preview.reset();
              preview.mutate(draft.value);
            }}
          >
            <Field label="Host" name="host" hint="@ forwards the domain itself">
              {(control) => (
                <Input
                  {...control}
                  value={draft.value.host}
                  disabled={draft.editingHost !== null}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="@ or subdomain, e.g. shop"
                  onChange={(e) => setDraft({ ...draft, value: { ...draft.value, host: e.target.value } })}
                />
              )}
            </Field>
            <Field label="Forward to" name="target_url" required>
              {(control) => (
                <Input
                  {...control}
                  type="url"
                  value={draft.value.target_url}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="https://example.com"
                  onChange={(e) =>
                    setDraft({ ...draft, value: { ...draft.value, target_url: e.target.value } })
                  }
                />
              )}
            </Field>
            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-[#5c5551]" id="redirect-label">
                Redirect type
              </p>
              <div role="radiogroup" aria-labelledby="redirect-label" className="flex gap-2">
                {(["301", "302"] as const).map((r) => (
                  <Button
                    unstyled
                    key={r}
                    type="button"
                    role="radio"
                    aria-checked={draft.value.redirect === r}
                    onClick={() => setDraft({ ...draft, value: { ...draft.value, redirect: r } })}
                    className={cx(
                      "rounded-lg px-3.5 py-2 text-[13px] font-medium",
                      draft.value.redirect === r
                        ? "bg-[#221c19] text-white"
                        : "border border-border bg-surface text-fg hover:bg-muted",
                    )}
                  >
                    {r} {r === "301" ? "(permanent)" : "(temporary)"}
                  </Button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-[13px] text-fg">
              <input
                type="checkbox"
                checked={draft.value.keep_path}
                onChange={(e) =>
                  setDraft({ ...draft, value: { ...draft.value, keep_path: e.target.checked } })
                }
                className="size-4 accent-brand-500"
              />
              Keep the page path (example.com/about → target.com/about)
            </label>
            <ErrorAlert error={preview.error} />
          </Form>
        </Modal>
      )}

      {review && (
        <BatchReviewModal
          open
          fqdn={domain.fqdn}
          title="Set up URL forwarding"
          description={`${fullHost(review.draft.host, domain.fqdn)} will forward to ${review.draft.target_url}.`}
          preview={review.preview}
          onBack={() => setReview(null)}
          onClose={closeReview}
          onConfirm={() => save.mutate(review.draft)}
          applying={save.isPending}
          error={save.error}
        />
      )}

      {removing && (
        <Modal
          open
          onClose={() => {
            if (remove.isPending) return;
            setRemoving(null);
            remove.reset();
          }}
          width={420}
          title="Remove this forwarding?"
          note="This can't be undone."
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="lg"
                loading={remove.isPending}
                onClick={() => remove.mutate(removing.host)}
              >
                Remove forwarding
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-[#5c5551]">
            {fullHost(removing.host, domain.fqdn)} will stop forwarding to {removing.target_url}.
          </p>
          <ErrorAlert error={remove.error} />
        </Modal>
      )}
    </Section>
  );
}
